import {ErrorType, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {
    ForwardedRef,
    forwardRef,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import {useTranslation} from 'react-i18next';
import Account from '../../../models/accountModel';
import {hasAdminLogin} from '../../../util/adminUtil';
import {trackPlatformAction} from '../../../util/analytics';
import {COUNTRIES} from '../../../util/countryUtil';
import {AccessKey, ActivateHandle, VATType, VATValue} from '../../../util/interfaces';
import {getDefaultVats, updateDefaultVATs} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import {CountryVATValues, CustomVAT, DefaultVAT} from '../../../util/vatUtil';
import {AppContext} from '../../misc/appContext';
import useRight from '../../misc/useRight';
import DefaultVatDialog from './defaultVatDialog';

const BASE_COUNTRY_CODES = ['NL', 'BE', 'DE'];

function serializeVats(values: CountryVATValues): string {
    return values.sortedCountryCodes.map(code => `${code}:${JSON.stringify(values.getCountryValue(code))}`).join('|');
}

function DefaultVatDialogController(_props: unknown, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [canEditVat] = useRight(AccessKey.PRODUCT_SETTINGS_EDIT_VAT);
    const [defaultVATs, setDefaultVATs] = useState<CountryVATValues>(new CountryVATValues());
    const [draft, setDraft] = useState<CountryVATValues>(new CountryVATValues());
    const [active, setActive] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, ErrorType>>({});
    const [removedCountryCodes, setRemovedCountryCodes] = useState<Array<string>>([]);
    const countryCodes = useMemo(() => {
        const codes = new Set(BASE_COUNTRY_CODES);
        for (const code of draft.sortedCountryCodes) codes.add(code);
        return Array.from(codes).filter(code => !removedCountryCodes.includes(code));
    }, [draft, removedCountryCodes]);

    const dirty = useMemo(
        () => !!removedCountryCodes.length || serializeVats(draft) !== serializeVats(defaultVATs),
        [draft, defaultVATs, removedCountryCodes]
    );
    const availableCountries = useMemo(
        () => Object.entries(COUNTRIES).filter(entry => !countryCodes.includes(entry[0])),
        [countryCodes]
    );
    const draftTouchedRef = useRef(false);

    function onOpen() {
        draftTouchedRef.current = false;
        setDraft(defaultVATs.clone());
        setErrors({});
        setRemovedCountryCodes([]);
        setActive(true);
        loadDefaultVats(true);
    }

    function onClose() {
        if (saving) return;
        setActive(false);
    }

    function onAddCountry(countryCode: string) {
        if (!countryCode.length) return;
        setValue(countryCode, DefaultVAT(0));
    }

    function onDeleteCountry(countryCode: string) {
        return function () {
            draftTouchedRef.current = true;
            setDraft(draft.clone().deleteCountryValue(countryCode));
            setRemovedCountryCodes(current => (current.includes(countryCode) ? current : [...current, countryCode]));
            setErrors(current => ({...current, [countryCode]: null}));
        };
    }

    function onClickValue(countryCode: string, value: VATValue) {
        return function () {
            setValue(countryCode, value);
        };
    }

    function onChangeCustom(countryCode: string) {
        return function (value: string) {
            const trimmed = value.trim();
            setValue(countryCode, CustomVAT(trimmed.length ? Number(trimmed) : 0));
        };
    }

    function setValue(countryCode: string, value: VATValue) {
        draftTouchedRef.current = true;
        setDraft(draft.clone().setCountryValue(countryCode, value));
        setRemovedCountryCodes(current => current.filter(code => code !== countryCode));
        setErrors(current => ({...current, [countryCode]: null}));
    }

    function onSubmit(retroactive?: boolean) {
        return function () {
            if (!account || saving || !validate()) return;
            if (retroactive === undefined) {
                showDialog(
                    t(
                        'settings.vat.retroactiveMsg',
                        'Wil je de gewijzigde BTW percentages met terugwerkende kracht toepassen? Hiermee worden alle facturen waar de gewijzigde producten op staan bijgewerkt.'
                    ),
                    t('settings.vat.applyChanges', 'Wijzigingen toepassen'),
                    [
                        {
                            label: t('settings.vat.noNewInvoicesOnly', 'Nee, alleen voor nieuwe facturen'),
                            onClick: onSubmit(false)
                        },
                        {
                            label: t('settings.vat.yesRetroactive', 'Ja, met terugwerkende kracht'),
                            onClick: onSubmit(true),
                            primary: false
                        }
                    ]
                );
                return;
            }
            setSaving(true);
            updateDefaultVATs({
                data: {account_id: account.id, default_vats: draft.dump(true), apply_retroactive: retroactive},
                callback: (data: ResponseData) => {
                    setSaving(false);
                    if (data.success) {
                        trackPlatformAction('products', 'save_default_vats');
                        setDefaultVATs(draft.clone());
                        setActive(false);
                        showSnackbar(t('settings.vat.defaultsSaved', 'De standaard BTW tarieven zijn opgeslagen'));
                    } else if (!data.handled) showSaveError(data.message);
                },
                errorCallback: () => {
                    setSaving(false);
                    showSaveError();
                }
            });
        };
    }

    function validate(): boolean {
        const next: Record<string, ErrorType> = {};
        for (const [countryCode, vatValue] of draft.entries()) {
            if (vatValue.vat_type !== VATType.CUSTOM) continue;
            if (vatValue.value > 100)
                next[countryCode] = t('settings.vat.errMaxPercent', 'Het percentage kan niet meer zijn dan 100%');
            else if (vatValue.value < 0)
                next[countryCode] = t('settings.vat.errNegativePercent', 'Het percentage kan niet negatief zijn');
        }
        setErrors(next);
        return !Object.keys(next).length;
    }

    function showSaveError(message?: string | null) {
        showDialog(
            createErrorMessage(
                message ??
                    t('settings.vat.saveDefaultsFailedMsg', 'Het opslaan van de standaard BTW tarieven is niet gelukt.')
            ),
            t('settings.vat.saveDefaultsFailedTitle', 'Opslaan mislukt')
        );
    }

    function loadDefaultVats(seedDraft = false) {
        if (!account) return;
        getDefaultVats({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (!data.success) return;
                const loaded = new CountryVATValues(data.default_vats);
                setDefaultVATs(loaded);
                if (seedDraft && !draftTouchedRef.current) setDraft(loaded.clone());
            }
        });
    }

    function saveAccountSetupProgress() {
        if (hasAdminLogin()) return;
        if (!account || account.setupProgress.vat) return;
        account.setupProgress.vat = true;
        account.update(undefined, undefined, undefined, (updated: Account, success: boolean) => {
            if (success) context.onChangeContext({account: updated});
            else account.setupProgress.vat = false;
        });
    }

    useEffect(() => {
        loadDefaultVats();
        saveAccountSetupProgress();
    }, [account?.id]);

    useImperativeHandle(ref, () => ({activate: onOpen}));

    return (
        <DefaultVatDialog
            onClose={onClose}
            onAddCountry={onAddCountry}
            onDeleteCountry={onDeleteCountry}
            onClickValue={onClickValue}
            onChangeCustom={onChangeCustom}
            onSubmit={onSubmit()}
            active={active}
            countryCodes={countryCodes}
            availableCountries={availableCountries}
            draft={draft}
            errors={errors}
            canEdit={canEditVat}
            dirty={dirty}
            saving={saving}
        />
    );
}

export default forwardRef<ActivateHandle>(DefaultVatDialogController);
